import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { basePrefix } from '../utils/format';
import type { GameCard, GameDetail, Profile, Toast, View, GamesSort } from '../types';

interface RoblokyContextType {
  view: View;
  goHome: () => void;
  openGame: (placeId: string, slug?: string) => void;
  openSaved: () => void;
  openProfile: () => void;

  games: GameCard[];
  gamesLoading: boolean;
  gamesLoadingMore: boolean;
  gamesHasMore: boolean;
  gamesQuery: string;
  setGamesQuery: (q: string) => void;
  gamesSort: GamesSort;
  setGamesSort: (s: GamesSort) => void;
  loadMoreGames: () => void;
  refreshGames: () => void;

  currentGame: GameDetail | null;
  gameLoading: boolean;
  refreshGame: () => void;
  likeGame: (placeId: string) => Promise<void>;
  saveGame: (placeId: string) => Promise<void>;
  commentOnGame: (placeId: string, text: string) => Promise<boolean>;

  savedGames: GameCard[];
  savedLoading: boolean;
  refreshSaved: () => void;

  profile: Profile | null;
  isAdmin: boolean;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: { displayName?: string; bio?: string }) => Promise<void>;

  isAuthModalOpen: boolean;
  authModalMode: 'signin' | 'signup' | 'guest';
  openAuthModal: (mode?: 'signin' | 'signup' | 'guest') => void;
  closeAuthModal: () => void;
  requireAuth: () => boolean;

  isAdminPanelOpen: boolean;
  setIsAdminPanelOpen: (v: boolean) => void;

  toasts: Toast[];
  addToast: (t: Omit<Toast, 'id' | 'timestamp'>) => void;
  removeToast: (id: string) => void;
}

const RoblokyContext = createContext<RoblokyContextType | null>(null);

// Parse an API response as JSON, but never let an HTML/proxy error page turn
// into a confusing empty failure â€” surface what the server actually answered.
async function readJson(res: Response): Promise<any> {
  try {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('json')) return { error: `Server answered ${res.status} (not JSON)` };
    return await res.json();
  } catch {
    return { error: `Server answered ${res.status}` };
  }
}

function viewFromUrl(): View {
  try {
    const m = window.location.pathname.match(/\/game\/(\d+)/);
    if (m) return { name: 'game', placeId: m[1] };
    if (window.location.pathname.endsWith('/robloky/saved')) return { name: 'saved' };
    if (window.location.pathname.endsWith('/robloky/profile')) return { name: 'profile' };
  } catch { /* ignore */ }
  return { name: 'home' };
}

export const RoblokyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: firebaseUser, authFetch, idToken } = useAuth();
  const [view, setView] = useState<View>(() => viewFromUrl());

  const [games, setGames] = useState<GameCard[]>([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gamesLoadingMore, setGamesLoadingMore] = useState(false);
  const [gamesHasMore, setGamesHasMore] = useState(false);
  const [gamesQuery, setGamesQuery] = useState('');
  const [gamesSort, setGamesSort] = useState<GamesSort>('latest');
  const nextBefore = useRef<number | null>(null);
  const listReq = useRef(0);

  const [currentGame, setCurrentGame] = useState<GameDetail | null>(null);
  const [gameLoading, setGameLoading] = useState(false);

  const [savedGames, setSavedGames] = useState<GameCard[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);

  const [profile, setProfile] = useState<Profile | null>(null);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup' | 'guest'>('signin');
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((t: Omit<Toast, 'id' | 'timestamp'>) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev.slice(-3), { ...t, id, timestamp: new Date().toISOString() }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 4200);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // â”€â”€ Navigation (state router + History API; server handles hard loads) â”€â”€
  // All URLs are prefixed with the portal base (/robloky in production).
  const pushUrl = useCallback((url: string) => {
    try {
      window.history.pushState({}, '', `${basePrefix()}${url}`);
    } catch { /* ignore */ }
  }, []);

  const goHome = useCallback(() => {
    pushUrl('/robloky/');
    setView({ name: 'home' });
    try {
      document.title = 'Robloky â€“ Discover Roblox Games | Screenshots, Live Stats & Reviews';
    } catch { /* ignore */ }
    window.scrollTo({ top: 0 });
  }, [pushUrl]);

  const openGame = useCallback((placeId: string, slug?: string) => {
    pushUrl(slug ? `/robloky/game/${placeId}/${slug}` : `/robloky/game/${placeId}`);
    setView({ name: 'game', placeId });
    window.scrollTo({ top: 0 });
  }, [pushUrl]);

  const openSaved = useCallback(() => {
    pushUrl('/robloky/saved');
    setView({ name: 'saved' });
    window.scrollTo({ top: 0 });
  }, [pushUrl]);

  const openProfile = useCallback(() => {
    pushUrl('/robloky/profile');
    setView({ name: 'profile' });
    window.scrollTo({ top: 0 });
  }, [pushUrl]);

  useEffect(() => {
    const onPop = () => setView(viewFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // â”€â”€ Games directory â”€â”€
  const fetchGames = useCallback(async (opts: { reset: boolean; q: string; sort: GamesSort }) => {
    const my = ++listReq.current;
    if (opts.reset) {
      setGamesLoading(true);
      nextBefore.current = null;
    } else {
      if (!nextBefore.current) return;
      setGamesLoadingMore(true);
    }
    try {
      const params = new URLSearchParams({
        q: opts.q,
        sort: opts.sort,
        limit: '24',
        ...(opts.reset || !nextBefore.current ? {} : { before: String(nextBefore.current) }),
      });
      const res = await authFetch(`/robloky/api/games?${params.toString()}`);
      const data = await res.json().catch(() => ({}));
      if (my !== listReq.current) return;
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load games');
      nextBefore.current = data.nextBefore ?? null;
      setGames((prev) => (opts.reset ? data.games : [...prev, ...data.games]));
      setGamesHasMore(data.nextBefore != null);
    } catch (e: any) {
      if (my === listReq.current && opts.reset) {
        addToast({ title: 'Games failed to load', message: e.message, type: 'error' });
      }
    } finally {
      if (my === listReq.current) {
        setGamesLoading(false);
        setGamesLoadingMore(false);
      }
    }
  }, [authFetch, addToast]);

  // Debounced search + sort reload
  useEffect(() => {
    const t = setTimeout(() => {
      fetchGames({ reset: true, q: gamesQuery, sort: gamesSort });
    }, gamesQuery ? 400 : 0);
    return () => clearTimeout(t);
  }, [gamesQuery, gamesSort, fetchGames, idToken]);

  const loadMoreGames = useCallback(() => {
    if (gamesLoadingMore || !nextBefore.current) return;
    fetchGames({ reset: false, q: gamesQuery, sort: gamesSort });
  }, [fetchGames, gamesLoadingMore, gamesQuery, gamesSort]);

  const refreshGames = useCallback(() => {
    fetchGames({ reset: true, q: gamesQuery, sort: gamesSort });
  }, [fetchGames, gamesQuery, gamesSort]);

  // â”€â”€ Game detail â”€â”€
  const fetchGame = useCallback(async (placeId: string) => {
    setGameLoading(true);
    try {
      const res = await authFetch(`/robloky/api/games/${placeId}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Game not found');
      setCurrentGame(data.game);
      // Canonicalize URL once the slug is known (server 301s it on hard loads too)
      try {
        const canon = `${basePrefix()}/game/${data.game.placeId}/${data.game.slug}`;
        if (window.location.pathname !== canon) window.history.replaceState({}, '', canon);
        document.title = `${data.game.title} - Play Free Online | Robloky`;
      } catch { /* ignore */ }
    } catch {
      setCurrentGame(null);
    } finally {
      setGameLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (view.name === 'game') fetchGame(view.placeId);
    else setCurrentGame(null);
  }, [view, fetchGame]);

  const refreshGame = useCallback(() => {
    if (view.name === 'game') fetchGame(view.placeId);
  }, [view, fetchGame]);

  const patchGame = useCallback((placeId: string, patch: Partial<GameCard & GameDetail>) => {    setGames((prev) => prev.map((g) => (g.placeId === placeId ? { ...g, ...patch } : g)));
    setCurrentGame((prev) => (prev && prev.placeId === placeId ? { ...prev, ...patch } : prev));
    setSavedGames((prev) => prev.map((g) => (g.placeId === placeId ? { ...g, ...patch } : g)));
  }, []);

  const requireAuth = useCallback(() => {
    if (!firebaseUser) {
      setAuthModalMode('signin');
      setIsAuthModalOpen(true);
      return false;
    }
    return true;
  }, [firebaseUser]);

  const likeGame = useCallback(async (placeId: string) => {
    if (!requireAuth()) return;
    try {
      const res = await authFetch(`/robloky/api/games/${placeId}/like`, { method: 'POST' });
      const data = await readJson(res);
      if (!res.ok || !data.success) throw new Error(data.error || `Like failed (${res.status})`);
      patchGame(placeId, { liked: data.liked, likes: data.likes });
    } catch (e: any) {
      addToast({ title: 'Like failed', message: e.message, type: 'error' });
    }
  }, [authFetch, patchGame, requireAuth, addToast]);

  const saveGame = useCallback(async (placeId: string) => {
    if (!requireAuth()) return;
    try {
      const res = await authFetch(`/robloky/api/games/${placeId}/save`, { method: 'POST' });
      const data = await readJson(res);
      if (!res.ok || !data.success) throw new Error(data.error || `Save failed (${res.status})`);
      patchGame(placeId, { saved: data.bookmarked });
      if (view.name === 'saved' && !data.bookmarked) {
        setSavedGames((prev) => prev.filter((g) => g.placeId !== placeId));
      }
      addToast({
        title: data.bookmarked ? 'Saved to your library' : 'Removed from library',
        message: '',
        type: 'success',
      });
    } catch (e: any) {
      addToast({ title: 'Save failed', message: e.message, type: 'error' });
    }
  }, [authFetch, patchGame, requireAuth, addToast, view.name]);

  const commentOnGame = useCallback(async (placeId: string, text: string) => {
    if (!requireAuth()) return false;
    try {
      const res = await authFetch(`/robloky/api/games/${placeId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      });
      const data = await readJson(res);
      if (!res.ok || !data.success) throw new Error(data.error || `Comment failed (${res.status})`);
      setCurrentGame((prev) => {
        if (!prev || prev.placeId !== placeId) return prev;
        return {
          ...prev,
          comments: prev.comments + 1,
          commentsList: [...prev.commentsList, data.comment],
        };
      });
      setGames((prev) => prev.map((g) => (g.placeId === placeId ? { ...g, comments: g.comments + 1 } : g)));
      return true;
    } catch (e: any) {
      addToast({ title: 'Comment failed', message: e.message, type: 'error' });
      return false;
    }
  }, [authFetch, requireAuth, addToast]);

  // â”€â”€ Saved â”€â”€
  const refreshSaved = useCallback(async () => {
    if (!firebaseUser) {
      setSavedGames([]);
      return;
    }
    setSavedLoading(true);
    try {
      const res = await authFetch('/robloky/api/saved');
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setSavedGames(data.games);
    } catch { /* ignore */ }
    finally {
      setSavedLoading(false);
    }
  }, [authFetch, firebaseUser]);

  useEffect(() => {
    if (view.name === 'saved') refreshSaved();
  }, [view.name, refreshSaved]);

  // â”€â”€ Profile (synced with the DB role) â”€â”€
  const refreshProfile = useCallback(async () => {
    if (!firebaseUser) {
      setProfile(null);
      return;
    }
    try {
      const res = await authFetch('/robloky/api/users/sync', { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.user) {
        const u = data.user;
        setProfile({
          uid: u.uid,
          email: u.email ?? null,
          displayName: u.displayName || 'Gamer',
          tag: u.tag || 'RED#0001',
          avatar: u.avatar || '',
          bio: u.bio || '',
          role: u.role === 'admin' ? 'admin' : 'user',
          verified: Boolean(u.verified),
        });
      }
    } catch { /* ignore */ }
  }, [authFetch, firebaseUser]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const updateProfile = useCallback(async (updates: { displayName?: string; bio?: string }) => {
    try {
      const res = await authFetch('/robloky/api/users/profile', { method: 'PUT', body: JSON.stringify(updates) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
      await refreshProfile();
      addToast({ title: 'Profile updated', message: '', type: 'success' });
    } catch (e: any) {
      addToast({ title: 'Update failed', message: e.message, type: 'error' });
    }
  }, [authFetch, refreshProfile, addToast]);

  const openAuthModal = useCallback((mode?: 'signin' | 'signup' | 'guest') => {
    setAuthModalMode(mode || 'signin');
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), []);

  // Keep the directory fresh in the background (new scout games appear live)
  useEffect(() => {
    const t = setInterval(() => {
      if (view.name === 'home' && !document.hidden) refreshGames();
    }, 120000);
    return () => clearInterval(t);
  }, [view.name, refreshGames]);

  return (
    <RoblokyContext.Provider
      value={{
        view,
        goHome,
        openGame,
        openSaved,
        openProfile,
        games,
        gamesLoading,
        gamesLoadingMore,
        gamesHasMore,
        gamesQuery,
        setGamesQuery,
        gamesSort,
        setGamesSort,
        loadMoreGames,
        refreshGames,
        currentGame,
        gameLoading,
        refreshGame,
        likeGame,
        saveGame,
        commentOnGame,
        savedGames,
        savedLoading,
        refreshSaved,
        profile,
        isAdmin: profile?.role === 'admin',
        refreshProfile,
        updateProfile,
        isAuthModalOpen,
        authModalMode,
        openAuthModal,
        closeAuthModal,
        requireAuth,
        isAdminPanelOpen,
        setIsAdminPanelOpen,
        toasts,
        addToast,
        removeToast,
      }}
    >
      {children}
    </RoblokyContext.Provider>
  );
};

export const useRobloky = () => {
  const ctx = useContext(RoblokyContext);
  if (!ctx) throw new Error('useRobloky must be used within RoblokyProvider');
  return ctx;
};
